import * as Sentry from '@sentry/react';
import { logger } from './logger';

export const PAYMENT_SUPPORT_CONTACT_SUFFIX = 'Please contact: hello@reaction-club.com';

/** Expected Stripe / cardholder outcomes — user-facing, not engineering bugs. */
const EXPECTED_PAYMENT_FAILURE_PATTERNS = [
    'card was declined',
    'card_declined',
    'insufficient funds',
    'insufficient_funds',
    'incorrect_cvc',
    'cvc is incorrect',
    'invalid security code',
    'incorrect_number',
    'expired_card',
    'authentication_required',
    'unable to authenticate your payment method',
    'payment method was declined',
    'your card has insufficient funds',
    'your card number is incorrect',
    'your card has expired',
    'requires_action',
    'failed to load stripe.js',
    'issues connecting to our payments provider',
];

export function isExpectedPaymentFailure(error: unknown): boolean {
    const message = (
        error instanceof Error ? error.message : String(error ?? '')
    ).toLowerCase();
    if (!message) return false;
    return EXPECTED_PAYMENT_FAILURE_PATTERNS.some((pattern) => message.includes(pattern));
}

export function appendPaymentSupportContact(message: string): string {
    const trimmed = message.trim();
    if (!trimmed) {
        return PAYMENT_SUPPORT_CONTACT_SUFFIX;
    }
    if (trimmed.includes('hello@reaction-club.com')) {
        return trimmed;
    }
    return `${trimmed} ${PAYMENT_SUPPORT_CONTACT_SUFFIX}`;
}

export function isStripePaymentEndpoint(endpoint: string): boolean {
    return endpoint.includes('/stripe/');
}

export interface PaymentFailureLogContext {
    flow: string;
    email?: string;
    metadata?: Record<string, unknown>;
}

const loggedPaymentFailures = new WeakSet<object>();

/** Prefer nested errorHandler shape `{ error: { message } }` over a generic top-level fallback. */
export function extractPaymentApiErrorMessage(data: unknown, fallback: string): string {
    if (!data || typeof data !== 'object') {
        return fallback;
    }

    const payload = data as {
        message?: unknown;
        error?: { message?: unknown; code?: unknown; details?: unknown } | string;
    };

    if (typeof payload.error === 'object' && payload.error && typeof payload.error.message === 'string') {
        const nestedMessage = payload.error.message.trim();
        if (nestedMessage) {
            return nestedMessage;
        }
    }

    if (typeof payload.error === 'string') {
        const stringError = payload.error.trim();
        if (stringError) {
            return stringError;
        }
    }

    if (typeof payload.message === 'string') {
        const topLevelMessage = payload.message.trim();
        if (topLevelMessage) {
            return topLevelMessage;
        }
    }

    return fallback;
}

export function logPaymentFailure(
    context: PaymentFailureLogContext,
    error: unknown
): void {
    const errorRecord = error instanceof Error ? error : new Error(String(error));
    if (loggedPaymentFailures.has(errorRecord)) {
        return;
    }
    loggedPaymentFailures.add(errorRecord);

    logger.error('[Payment]', context.flow, errorRecord.message, context.metadata);

    // Card declines / auth failures are expected customer outcomes — log only.
    if (isExpectedPaymentFailure(errorRecord)) {
        return;
    }

    Sentry.captureException(errorRecord, {
        tags: { payment_flow: context.flow },
        extra: {
            email: context.email,
            ...context.metadata,
        },
    });
}

export function formatPaymentErrorMessage(message: string): string {
    return appendPaymentSupportContact(message);
}
